import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@charitystack/shared';
import { loadChecklistVisibility, saveChecklistVisibility } from '../../../api/setupChecklistPreferences';

const SetupChecklistPreferences = createContext(null);
const emptyPreferences = () => ({ completed: {}, dismissed: false, reopened: false, minimized: false });
const legacyKey = (scope) => `cs:setup-checklist:${scope}`;
const canonicalKey = (scope) => `cs:setup-checklist:v2:${scope}`;

function readPreferences(key) {
  // A denied read is not an absent preference; let the provider retain session-only state.
  const serialized = localStorage.getItem(key);
  try {
    const saved = JSON.parse(serialized);
    if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return null;
    if (['dismissed', 'reopened', 'minimized'].some((field) => saved[field] !== undefined && typeof saved[field] !== 'boolean')) return null;
    if (saved.completed !== undefined && (!saved.completed || typeof saved.completed !== 'object' || Array.isArray(saved.completed))) return null;
    return {
      completed: Object.fromEntries(Object.entries(saved.completed || {}).filter(([, value]) => typeof value === 'boolean')),
      dismissed: saved.dismissed === true,
      reopened: saved.reopened === true,
      minimized: saved.minimized === true,
    };
  } catch {
    return null;
  }
}

/** Personal UI preferences only; never writes merchant onboarding or payment state. */
export function SetupChecklistPreferencesProvider({ scope, legacyScope, ready = true, userId, organizationId, children }) {
  const accountEnabled = !!userId && !!organizationId && scope === `${userId}:${organizationId}`;
  // A visit generation rejects actions from A even after an A -> B -> A switch.
  const visit = useRef({ scope, ready, generation: 0, aliases: new Set(), storageReadable: true });
  if (visit.current.scope !== scope || visit.current.ready !== ready) {
    visit.current = { scope, ready, generation: visit.current.generation + 1, aliases: new Set(), storageReadable: true };
  }
  const generation = visit.current.generation;
  if (scope && ready) {
    visit.current.aliases.add(scope);
    if (legacyScope) visit.current.aliases.add(legacyScope);
  }
  const saved = useMemo(() => {
    if (!scope || !ready) return null;
    try {
      return readPreferences(canonicalKey(scope))
        || (legacyScope && readPreferences(legacyKey(legacyScope)))
        || readPreferences(legacyKey(scope))
        || emptyPreferences();
    } catch {
      visit.current.storageReadable = false;
      return emptyPreferences();
    }
  }, [scope, legacyScope, ready]);
  const [snapshot, setSnapshot] = useState(null);
  const [openRequest, setOpenRequest] = useState(null);
  const [account, setAccount] = useState(null);
  const [loadRetry, setLoadRetry] = useState(0);
  const [retry, setRetry] = useState(0);
  const accountPending = accountEnabled && (account?.generation !== generation || account.status === 'loading');
  const baseline = useMemo(() => saved && ({
    ...saved,
    ...(account?.generation === generation ? account.visibility : null),
  }), [saved, account, generation]);
  const preferences = snapshot?.generation === generation ? snapshot.preferences : baseline;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const isCurrentVisit = useCallback(() => mounted.current && visit.current.generation === generation && ready && !!scope, [generation, ready, scope]);
  const update = useCallback((patch, forceVisibilitySave = false) => {
    if (!isCurrentVisit()) return;
    setSnapshot((previous) => {
      if (!isCurrentVisit()) return previous;
      const current = previous?.generation === generation ? previous.preferences : baseline;
      const changes = typeof patch === 'function' ? patch(current) : patch;
      const next = { ...current, ...changes };
      const visibilityChanged = forceVisibilitySave || next.dismissed !== current.dismissed || next.reopened !== current.reopened;
      return { generation, preferences: next, revision: (previous?.generation === generation ? previous.revision : 0) + Number(visibilityChanged) };
    });
  }, [generation, isCurrentVisit, baseline]);
  const localPreferences = useRef(saved);
  localPreferences.current = saved;
  useEffect(() => {
    if (!accountEnabled || !isCurrentVisit()) return;
    let cancelled = false;
    setAccount({ generation, status: 'loading', visibility: null });
    const load = async () => {
      let visibility = await loadChecklistVisibility(userId, organizationId);
      if (cancelled || !isCurrentVisit()) return;
      const local = localPreferences.current;
      // Only migrate an explicit old choice, after a successful account read.
      // The save rereads the account and preserves an already-established choice.
      if (!visibility && (local?.dismissed || local?.reopened)) {
        visibility = await saveChecklistVisibility(userId, organizationId, {
          dismissed: local.dismissed, reopened: local.reopened,
        }, { initializeOnly: true });
      }
      if (!cancelled && isCurrentVisit()) {
        // Retain any visibility action from this visit: its save may still be in
        // flight. A retry can refresh visibility when only local task checks changed.
        if (visibility) setSnapshot((previous) => previous?.generation === generation && previous.revision === 0
          ? { ...previous, preferences: { ...previous.preferences, ...visibility } } : previous);
        setAccount({ generation, status: 'ready', visibility });
      }
    };
    load().catch(() => {
      if (cancelled || !isCurrentVisit()) return;
      setAccount({ generation, status: 'error', visibility: null });
      toast.error('Couldn’t load your account’s checklist preference. Using this browser’s saved choice.', {
        action: { label: 'Retry', onClick: () => {
          if (isCurrentVisit()) setLoadRetry((value) => value + 1);
        } },
      });
    });
    return () => { cancelled = true; };
  }, [accountEnabled, generation, isCurrentVisit, loadRetry, organizationId, userId]);
  const lastWrite = useRef(null);
  useEffect(() => {
    if (!accountEnabled || !isCurrentVisit() || snapshot?.generation !== generation || !snapshot.revision) return;
    const signature = `${generation}:${snapshot.revision}:${retry}`;
    if (lastWrite.current === signature) return;
    lastWrite.current = signature;
    saveChecklistVisibility(userId, organizationId, {
      dismissed: snapshot.preferences.dismissed, reopened: snapshot.preferences.reopened,
    }).catch(() => {
      if (!isCurrentVisit() || lastWrite.current !== signature) return;
      toast.error('Checklist preference saved in this browser only. Couldn’t sync to your account.', {
        action: { label: 'Retry', onClick: () => {
          if (isCurrentVisit() && lastWrite.current === signature) setRetry((value) => value + 1);
        } },
      });
    });
  }, [accountEnabled, generation, isCurrentVisit, organizationId, retry, snapshot, userId]);
  useEffect(() => {
    if (!preferences || accountPending || !isCurrentVisit() || !visit.current.storageReadable) return;
    // Retain verified old aliases for rollback; never discover aliases by scanning storage.
    for (const key of [canonicalKey(scope), ...[...visit.current.aliases].map(legacyKey)]) {
      try { localStorage.setItem(key, JSON.stringify(preferences)); } catch { /* Session state remains usable when storage is blocked. */ }
    }
  }, [preferences, accountPending, legacyScope, scope, isCurrentVisit]);
  const value = scope && ready && preferences && !accountPending ? {
    ...preferences,
    openRequest: openRequest?.generation === generation ? openRequest.id : 0,
    update,
    dismiss: () => update({ dismissed: true, reopened: false }, true),
    reopen: () => {
      if (!isCurrentVisit()) return;
      update({ dismissed: false, reopened: true, minimized: false }, true);
      setOpenRequest((previous) => ({ generation, id: (previous?.generation === generation ? previous.id : 0) + 1 }));
    },
  } : null;
  return <SetupChecklistPreferences.Provider value={value}>{children}</SetupChecklistPreferences.Provider>;
}

export function useSetupChecklistPreferences() {
  return useContext(SetupChecklistPreferences);
}
